import React, { useEffect, useState } from 'react';
import type { CollapseProps, MenuProps } from 'antd';
import {
  Button,
  Col,
  List,
  Modal,
  Row,
  Form,
  Input,
  message,
  ConfigProvider,
  Select,
  Collapse,
  Typography,
  Dropdown,
  DatePicker,
  Skeleton,
  Grid,
  Avatar,
  Flex,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  BackwardOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  LogoutOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Flag from 'react-world-flags';
import '../Assets/Styles/Header.module.css';
import type {
  UpdateUserDTO,
  User,
  StudyLine,
  Student,
} from '../Types/interfaces';
import { GetStudyLines, UpdateStudent, UpdateUser } from '../helper/apiHelper';
import { validateMobileNumber } from '../Validators/ValidatorMobileNumber';
import { validateDateSmallerThanToday } from '../Validators/validateDateSmallerThanToday';
import { externalInstance, ubbLoginInstance } from '../helper/authHelper';
import { useHasRole } from '../hooks/useHasRole';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { RiLogoutBoxRLine } from 'react-icons/ri';
import NotificationCenterBell from './NotificationCenterBell';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DATE_FORMAT } from '../Constants/constants';
import { useStudent } from '../hooks/useStudent';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../Constants/queryKeys';
import { useUserOrganizations } from '../hooks/useOrganizations';
import { useCurrentUser } from '../hooks/useCurrentUser';
import logo from '../Assets/Images/Logo-Babes-Bolyai.png';
import Title from 'antd/lib/typography/Title';
import { MdSunny } from 'react-icons/md';
import { FaMoon } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';

const { useBreakpoint } = Grid;

dayjs.extend(customParseFormat);

const EMPTY_ARRAY: StudyLine[] = [];

const StudentFieldNames: Partial<Student> = {
  city: 'city',
  street: 'street',
  streetNumber: 'streetNumber',
  county: 'county',
  cnp: 'cnp',
  idSeries: 'idSeries',
  idNumber: 'idNumber',
  dateOfBirth: 'dateOfBirth',
  placeOfBirth: 'placeOfBirth',
  group: 'group',
  nationality: 'nationality',
};

const Roles = {
  None: 0,
  Student: 1,
  'Cadru Didactic': 2,
  Administrator: 4,
  'Reprezentant Firma Organzatie': 8,
  Tutore: 16,
  'Reprezentant Departament': 32,
  Decan: 64,
  'Departament Juridic': 128,
  'Responsabil Protectia Muncii': 256,
  Rector: 512,
  'Coordonator Universitar': 1024,
};

const renderWrappingLabel = (text: string): React.ReactNode => (
  <span style={{ display: 'inline-block', whiteSpace: 'normal' }}>{text}</span>
);

const getFlagByLanguage = (lang: string | undefined): React.ReactNode => {
  const flagWidth = 25;
  switch (lang) {
    case 'ro':
      return <Flag width={flagWidth} height="auto" code="RO" />;
    case 'hu':
      return <Flag width={flagWidth} height="auto" code="HU" />;
    case 'en':
      return <Flag width={flagWidth} height="auto" code="GB" />;
    case 'de':
      return <Flag width={flagWidth} height="auto" code="DE" />;
    default:
      return <Flag width={flagWidth} height="auto" code="GB" />;
  }
};

const buttonStyle: React.CSSProperties = {
  color: 'white',
  border: '1px solid white',
  borderRadius: '10px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
};

interface HeaderComponentProps {
  isLoginPage?: boolean;
  collapsed?: boolean;
  setCollapsed?: (value: boolean) => void;
}

const HeaderComponent = (props: HeaderComponentProps): React.ReactNode => {
  const { t, i18n } = useTranslation();
  const hasRole = useHasRole();
  const loggedInUser = useCurrentUser();
  const [visibleModal, setVisibleModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [studentForm] = Form.useForm<Partial<Student>>();
  const [userForm] = Form.useForm();
  const watchedUserFormValues = Form.useWatch([], userForm) || {};
  const watchedStudentFormValues = Form.useWatch([], studentForm) || {};
  const { data: student, isLoading: isStudentLoading } = useStudent(
    loggedInUser?.id
  );
  const { data: userOrganizations, isLoading: isUserOrganizationsLoading } =
    useUserOrganizations(loggedInUser?.id);
  const queryClient = useQueryClient();
  const screens = useBreakpoint();
  const { isDarkMode, toggleTheme } = useTheme();

  const { data: studyLines = EMPTY_ARRAY, isLoading: isStudyLinesLoading } =
    useQuery({
      queryKey: [...QUERY_KEYS.STUDY_LINES],
      queryFn: async () => {
        return await GetStudyLines();
      },
      meta: {
        onError: () => {
          messageApi.error(t('header.error.getStudyLines'));
        },
      },
      enabled: !!student?.id,
    });

  useEffect(() => {
    if (loggedInUser) {
      userForm.setFieldsValue({
        firstName: loggedInUser.firstName,
        lastName: loggedInUser.lastName,
        phoneNumber: loggedInUser.phoneNumber,
        email: loggedInUser.email,
        jobTitle: loggedInUser ? loggedInUser.jobTitle : '',
      });
    }
  }, [loggedInUser, userForm]);

  useEffect(() => {
    if (student && student.id !== 0) {
      studentForm.setFieldsValue({
        city: student?.city,
        street: student?.street,
        streetNumber: student?.streetNumber,
        county: student?.county,
        cnp: student?.cnp,
        idSeries: student?.idSeries,
        idNumber: student?.idNumber,
        dateOfBirth: student?.dateOfBirth,
        placeOfBirth: student?.placeOfBirth,
        group: student?.group,
        nationality: student?.nationality,
        preferredStudyLineIds: student?.preferredStudyLineIds,
      });
    }
  }, [student, studentForm]);

  const updateStudentProfileMutation = useMutation({
    mutationFn: async (updateStudentDTO: Partial<Student>) => {
      if (!student) return;
      return await UpdateStudent(student.id, updateStudentDTO);
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.STUDENT_DATA, loggedInUser?.id],
      });
    },
    onError: () => {},
  });

  const updateUserProfileMutation = useMutation({
    mutationFn: async (updateUserDTO: UpdateUserDTO) => {
      if (!loggedInUser) return;
      return await UpdateUser(loggedInUser.id, updateUserDTO);
    },
    onSuccess: (_, variables: UpdateUserDTO) => {
      queryClient.setQueryData(QUERY_KEYS.USER, (old: User) => ({
        ...old,
        ...variables,
      }));
    },
    onError: () => {},
  });

  const isLoading =
    ((hasRole(Roles.Student) && (isStudentLoading || isStudyLinesLoading)) ||
      isUserOrganizationsLoading ||
      (loggedInUser && loggedInUser.id === 0)) &&
    !props.isLoginPage;

  // login skeleton while data downloading
  if (isLoading) {
    return (
      <Row align="middle" justify="space-between" wrap>
        {/* Collapse sidebar button */}
        <Col>{!screens.lg && <Skeleton.Button active />}</Col>

        {/* Logo */}
        <Col xs={12} sm={8} md={6} lg={4}>
          <Skeleton.Image
            style={{ width: screens.lg ? 300 : 200, height: 50 }}
            active
          />
        </Col>

        {/* This isn't the login page, it's the user page */}
        {!props.isLoginPage && (
          <>
            {screens.lg ? (
              <Col xs={12} sm={16} md={18} lg={20} flex="1 1 auto">
                <Row
                  style={{ padding: '10px 10px 10px 0px' }}
                  justify="end"
                  align="middle"
                  gutter={[8, 8]}
                  wrap
                >
                  {/* Profile button */}
                  {!props.isLoginPage && <Skeleton.Button active />}
                </Row>
              </Col>
            ) : (
              <Col
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  paddingRight: '16px',
                }}
              >
                <Skeleton.Avatar active />
              </Col>
            )}

            <Col>
              <Skeleton.Avatar active />
            </Col>
          </>
        )}

        {/* Select language button */}
        {screens.lg && (
          <Col
            style={{
              padding: props.isLoginPage ? '0 1.5% 0 1.5%' : '0px 5px 0px 5px',
            }}
          >
            <Skeleton.Button active />
          </Col>
        )}

        {/* Logout button */}
        {!props.isLoginPage && screens.lg && (
          <Col style={{ padding: '0px 5px 0px 5px' }}>
            <Skeleton.Button active />
          </Col>
        )}
      </Row>
    );
  }

  const requiredUserDataFields: (keyof User)[] = [
    'firstName',
    'lastName',
    'phoneNumber',
  ];

  const requiredStudentFields: (keyof Student)[] = [
    'city',
    'street',
    'streetNumber',
    'county',
    'cnp',
    'idSeries',
    'idNumber',
    'dateOfBirth',
    'placeOfBirth',
    'group',
    'nationality',
  ];

  // no students
  const requiredUserDataFieldsForOther: (keyof User)[] = [
    'firstName',
    'lastName',
    'phoneNumber',
    'jobTitle',
  ];

  const isRequiredUserDataEmpty = isEditMode
    ? requiredUserDataFields.some((field) => !watchedUserFormValues[field])
    : requiredUserDataFields.some((field) => !loggedInUser?.[field]);

  const isRequiredUserDataForNonStudentsEmpty = isEditMode
    ? requiredUserDataFieldsForOther.some(
        (field) => !watchedUserFormValues[field]
      )
    : requiredUserDataFieldsForOther.some((field) => !loggedInUser?.[field]);

  const isStudentInfoEmpty = isEditMode
    ? requiredStudentFields.some((field) => !watchedStudentFormValues[field])
    : requiredStudentFields.some((field) => !student?.[field]);

  const isDataMissing =
    !!loggedInUser.id &&
    ((hasRole(Roles.Student) &&
      (isStudentInfoEmpty || isRequiredUserDataEmpty)) ||
      (!hasRole(Roles.Student) && isRequiredUserDataForNonStudentsEmpty));

  const isClosableModal = !isDataMissing && !isEditMode;
  const isModalOpen =
    (visibleModal || isDataMissing || isEditMode) && !!loggedInUser.id;

  const pickRoles = (): string => {
    let roleStr = '';
    for (const [key, value] of Object.entries(Roles)) {
      if ((loggedInUser ? loggedInUser.roles & value : '') === value) {
        roleStr = roleStr + key + ', ';
      }
    }

    return roleStr.slice(0, -2);
  };

  const handleOk = (): void => {
    setVisibleModal(false);
  };

  const handleCancel = (): void => {
    setVisibleModal(false);
    setIsEditMode(false);
  };

  const handleEdit = (): void => {
    setIsEditMode(!isEditMode);
  };

  const handleLogout = async (): Promise<void> => {
    // This will get the active user even if the login happend with the external instance if the page was refreshed
    let user = ubbLoginInstance.getActiveAccount();

    if (user?.environment !== undefined) {
      const authority = user.environment || '';
      // Check authority to use the proper instance
      if (authority.startsWith('login.windows.net')) {
        await ubbLoginInstance.logoutPopup({
          account: user,
          postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI,
        });
        // redirect to login page
        window.location.href = process.env.REACT_APP_REDIRECT_URI ?? '/';
      } else {
        await externalInstance.logoutPopup({
          account: user,
          postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI,
        });
        window.location.href = process.env.REACT_APP_REDIRECT_URI ?? '/';
      }
    } else {
      user = externalInstance.getActiveAccount();
      if (user) {
        await externalInstance.logoutPopup({
          account: user,
          postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI,
        });
        window.location.href = process.env.REACT_APP_REDIRECT_URI ?? '/';
      } else {
        console.warn('No active user found.');
      }
    }
  };

  const updateStudentOnSave = async (): Promise<boolean> => {
    if (!student || student.id === 0) return false;
    const isFormModified = studentForm.isFieldsTouched();

    if (!isFormModified) return false;

    const updateStudentDTO: Partial<Student> = studentForm.getFieldsValue();

    await updateStudentProfileMutation.mutateAsync(updateStudentDTO);
    return true;
  };

  const updateUserOnSave = async (): Promise<boolean> => {
    if (!loggedInUser) return false;
    const isFormModified = userForm.isFieldsTouched();

    if (!isFormModified) return false;

    const formValues = userForm.getFieldsValue();

    const phoneNumberObj = parsePhoneNumberFromString(
      formValues.phoneNumber ?? '',
      'RO'
    );
    const updateUserDTO: UpdateUserDTO = {
      firstName: formValues.firstName ?? '',
      lastName: formValues.lastName ?? '',
      phoneNumber: phoneNumberObj
        ? phoneNumberObj.format('E.164')
        : (formValues.phoneNumber ?? ''),
      email: loggedInUser?.email ?? '',
      roles: loggedInUser.roles,
      jobTitle: formValues.jobTitle ?? '',
    };

    await updateUserProfileMutation.mutateAsync(updateUserDTO);
    return true;
  };

  const handleSave = async (): Promise<void> => {
    let didUserUpdate = undefined;
    let didStudentUpdate = undefined;
    try {
      await userForm.validateFields();

      if (hasRole(Roles.Student)) {
        await studentForm.validateFields();
      }

      didStudentUpdate = await updateStudentOnSave();
      didUserUpdate = await updateUserOnSave();

      if (didStudentUpdate || didUserUpdate) {
        await queryClient.refetchQueries({
          queryKey: [...QUERY_KEYS.STUDENT_DATA, loggedInUser?.id],
        });

        messageApi.success({
          content: t('header.message.success'),
        });
        setVisibleModal(false);
        setIsEditMode(false);
      } else {
        messageApi.info({
          content: t('header.message.noModification'),
        });
      }
    } catch (error: unknown) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'errorFields' in error
      ) {
        messageApi.error({ content: t('header.validateError') });
        return;
      }

      if (didUserUpdate !== undefined) {
        messageApi.error({
          content: t('header.message.errorStudent') + error,
        });
      } else {
        messageApi.error({
          content: t('header.message.errorUser') + error,
        });
      }
    }
  };

  const handleChangeLanguageFlag = (value: string): void => {
    switch (value) {
      case 'ro':
        i18n.changeLanguage('ro');
        localStorage.setItem('language', 'ro');
        break;
      case 'hu':
        i18n.changeLanguage('hu');
        localStorage.setItem('language', 'hu');
        break;
      case 'en':
        i18n.changeLanguage('en');
        localStorage.setItem('language', 'en');
        break;
      case 'de':
        i18n.changeLanguage('de');
        localStorage.setItem('language', 'de');
        break;
    }
  };

  const languageMenuItems: MenuProps['items'] = [
    {
      key: 'ro',
      label: 'RO',
      icon: getFlagByLanguage('ro'),
    },
    {
      key: 'hu',
      label: 'HU',
      icon: getFlagByLanguage('hu'),
    },
    {
      key: 'en',
      label: 'EN',
      icon: getFlagByLanguage('en'),
    },
    {
      key: 'de',
      label: 'DE',
      icon: getFlagByLanguage('de'),
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    handleChangeLanguageFlag(e.key);
  };

  const menuProps: MenuProps = {
    items: languageMenuItems,
    onClick: handleMenuClick,
  };

  const items: CollapseProps['items'] = [
    {
      key: 'information',
      label: (
        <>
          {isStudentInfoEmpty ? (
            <Typography.Text type="danger">
              {t('header.userForm.fillInfo')}
            </Typography.Text>
          ) : (
            t('header.information')
          )}
        </>
      ),
      children: (
        <>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.city') + ':')}
            name={StudentFieldNames.city}
            colon={false}
            required={false} // remove ugly red star
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.city'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.street') + ':')}
            name={StudentFieldNames.street}
            colon={false}
            required={false}
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.street'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.streetNumber') + ':')}
            name={StudentFieldNames.streetNumber}
            colon={false}
            required={false}
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.streetNumber'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.county') + ':')}
            name={StudentFieldNames.county}
            colon={false}
            required={false}
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.county'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.cnp') + ':')}
            name={StudentFieldNames.cnp}
            colon={false}
            required={false}
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.cnp'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.idSeries') + ':')}
            name={StudentFieldNames.idSeries}
            colon={false}
            required={false}
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.idSeries'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.idNumber') + ':')}
            name={StudentFieldNames.idNumber}
            colon={false}
            required={false}
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.idNumber'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.dateOfBirth') + ':')}
            name={StudentFieldNames.dateOfBirth}
            required={false}
            rules={[
              {
                validator: validateDateSmallerThanToday,
                message: t('validator.dateInFuture'),
              },
              {
                required: true,
                message: t('header.userForm.requiredMessage.dateOfBirth'),
              },
            ]}
            colon={false}
            style={{ marginBottom: '8px' }}
            // when the date changes, convert immediately to an ISO string for the form
            getValueFromEvent={(date) => {
              return date ? date.format('YYYY-MM-DD') : '';
            }}
            // when the picker show value, convert string to the Dayjs obj
            getValueProps={(value) => {
              if (!value) return { value: null };

              // read the date with actual language format
              let parsedDate = dayjs(value, DATE_FORMAT[i18n.language]);

              // If the read date is not in the correct format, read it in ISO format
              if (!parsedDate.isValid()) {
                parsedDate = dayjs(value);
              }

              return { value: parsedDate.isValid() ? parsedDate : null };
            }}
          >
            <DatePicker
              format={{
                format: DATE_FORMAT[i18n.language] ?? DATE_FORMAT['en'],
                type: 'mask',
              }}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.placeOfBirth') + ':')}
            name={StudentFieldNames.placeOfBirth}
            colon={false}
            required={false}
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.placeOfBirth'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.group') + ':')}
            name={StudentFieldNames.group}
            colon={false}
            required={false}
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.group'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={renderWrappingLabel(t('header.userForm.nationality') + ':')}
            name={StudentFieldNames.nationality}
            colon={false}
            required={false}
            rules={[
              {
                required: true,
                message: t('header.userForm.requiredMessage.nationality'),
              },
            ]}
            style={{ marginBottom: '8px' }}
          >
            <Input placeholder={'român'} />
          </Form.Item>
        </>
      ),
    },
  ];

  const studentFormItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  const ThemeSwitchButton = (
    <Button
      type="text"
      onClick={toggleTheme}
      icon={
        isDarkMode ? (
          <MdSunny style={{ fontSize: 20, color: 'white' }} />
        ) : (
          <FaMoon style={{ fontSize: 20, color: 'white' }} />
        )
      }
    />
  );

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: `${loggedInUser.lastName} ${loggedInUser.firstName}`,
      onClick: () => setVisibleModal(true),
    },
    {
      key: 'language',
      icon: <GlobalOutlined />,
      label: t('header.language'),
      children: languageMenuItems?.map((item: any) => ({
        ...item,
        onClick: handleMenuClick,
      })),
    },
    {
      key: 'switchTheme',
      icon: isDarkMode ? (
        <MdSunny style={{ fontSize: 15, color: 'white' }} />
      ) : (
        <FaMoon style={{ fontSize: 15, color: 'dark' }} />
      ),
      label: t('header.themeSwitchOnMobile'),
      onClick: toggleTheme,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('header.logout'),
      danger: true,
      onClick: async () => await handleLogout(),
    },
  ];

  const ModalForProfile: React.ReactNode = (
    <Modal
      title={
        loggedInUser?.lastName || loggedInUser?.firstName
          ? `${loggedInUser?.lastName} ${loggedInUser?.firstName}`.trim()
          : loggedInUser?.email
      }
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      maskClosable={isClosableModal}
      closable={isClosableModal}
      keyboard={isClosableModal}
      footer={[
        <Button
          key="edit"
          onClick={handleEdit}
          icon={!isEditMode ? <EditOutlined /> : <BackwardOutlined />}
        >
          {!isEditMode
            ? t('header.userFormControls.edit')
            : t('header.userFormControls.back')}
        </Button>,
        isEditMode && (
          <Button key="save" type="primary" onClick={handleSave}>
            {t('header.userFormControls.save')}
          </Button>
        ),
      ]}
    >
      <>
        <div style={{ marginBottom: '16px' }}>
          {(isRequiredUserDataEmpty ||
            (isRequiredUserDataForNonStudentsEmpty &&
              !hasRole(Roles.Student))) && (
            <Typography.Text
              type="danger"
              style={{
                display: 'flex',
                justifyContent: 'center',
                fontSize: '16px',
                padding: '10px',
              }}
            >
              {t('header.userForm.fillInfo')}
            </Typography.Text>
          )}
          <Form
            form={userForm}
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            style={{ textAlign: 'left' }}
            layout="horizontal"
            disabled={!isEditMode}
            labelAlign="left"
          >
            <Form.Item
              label={renderWrappingLabel(t('header.userForm.lastName') + ':')}
              name="lastName"
              colon={false}
              required={false}
              rules={[
                {
                  required: true,
                  message: t('header.userForm.requiredMessage.lastName'),
                },
              ]}
              style={{ marginBottom: '8px' }}
              labelAlign="left"
            >
              <Input placeholder={t('header.userForm.placeholderLastName')} />
            </Form.Item>
            <Form.Item
              label={renderWrappingLabel(t('header.userForm.firstName') + ':')}
              name="firstName"
              colon={false}
              required={false}
              rules={[
                {
                  required: true,
                  message: t('header.userForm.requiredMessage.firstName'),
                },
              ]}
              style={{ marginBottom: '8px' }}
              labelAlign="left"
            >
              <Input placeholder={t('header.userForm.placeholderFirstName')} />
            </Form.Item>
            <Form.Item
              label={renderWrappingLabel(t('header.userForm.tel') + ':')}
              name="phoneNumber"
              colon={false}
              required={false}
              style={{ marginBottom: '8px' }}
              rules={[
                {
                  validator: validateMobileNumber,
                },
                {
                  required: true,
                  message: t('header.userForm.requiredMessage.phoneNumber'),
                },
              ]}
            >
              <Input />
            </Form.Item>
            {!hasRole(Roles.Student) && (
              <Form.Item
                label={renderWrappingLabel(t('header.userForm.jobTitle') + ':')}
                name="jobTitle"
                colon={false}
                required={false}
                style={{ marginBottom: '8px' }}
              >
                <Input />
              </Form.Item>
            )}
          </Form>
          {student !== null &&
            student !== undefined &&
            studyLines.length > 0 &&
            hasRole(Roles.Student) && (
              <Form
                form={studentForm}
                {...studentFormItemLayout}
                style={{ textAlign: 'left' }}
                layout="horizontal"
                disabled={!isEditMode}
                labelAlign="left"
              >
                <Form.Item
                  label={renderWrappingLabel(
                    t('header.userForm.seldStudyLines') + ':'
                  )}
                  name="preferredStudyLineIds"
                  colon={false}
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'header.userForm.requiredMessage.preferredStudyLineIds'
                      ),
                    },
                  ]}
                  style={{ marginBottom: '8px' }}
                  labelAlign="left"
                >
                  <Select
                    mode="multiple"
                    placeholder={t('header.userForm.selStudyLines')}
                    defaultValue={
                      studyLines?.length === 1 ? studyLines[0].id : null
                    }
                    options={studyLines.map((sl) => ({
                      label: `${sl.name}`,
                      value: sl.id,
                    }))}
                  />
                </Form.Item>
                <Collapse ghost items={items} />
              </Form>
            )}
        </div>
        <div style={{ marginBottom: '8px' }}>
          {t('header.email')}: {loggedInUser?.email}
        </div>
        <div style={{ marginBottom: '8px' }}>{t('header.orgs')}:</div>
        <ConfigProvider
          renderEmpty={() => {
            return (
              <div
                style={{
                  textAlign: 'left',
                  lineHeight: 0.25,
                }}
              >
                No Data
              </div>
            );
          }}
        >
          <List
            itemLayout="horizontal"
            dataSource={userOrganizations ?? []}
            renderItem={(item) => (
              <List.Item style={{ marginBottom: '8px' }}>
                <List.Item.Meta title={item.name} />
              </List.Item>
            )}
          />
        </ConfigProvider>
        <div style={{ marginBottom: '8px' }}>{t('header.roles')}</div>
        <div style={{ marginBottom: '16px' }}>{pickRoles()}</div>
      </>
    </Modal>
  );

  const ProfileButton: React.ReactElement = (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      }}
    >
      <Button
        type="text"
        size="large"
        onClick={() => setVisibleModal(true)}
        ghost
        style={{ ...buttonStyle }}
      >
        {isDataMissing && (
          <ExclamationCircleOutlined
            style={{ marginLeft: '5px', color: 'red' }}
          />
        )}
        {`${loggedInUser?.lastName} ${loggedInUser?.firstName}`}
        <UserOutlined />
      </Button>
    </div>
  );

  const CollapseSideBarButton: React.ReactElement = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: props.collapsed ? 'center' : 'flex-end',
        flexShrink: 0,
      }}
    >
      <Button
        type="text"
        icon={props.collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        style={{
          fontSize: '20px',
          color: '#fff',
          width: 32,
          height: 32,
        }}
        onClick={() => props.setCollapsed?.(!props.collapsed)}
      ></Button>
    </div>
  );

  const Logo: React.ReactElement = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: !props.isLoginPage && !screens.lg ? 28 : 2,
        paddingTop: 3,
        paddingBottom: 3,
      }}
    >
      <a
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        href={`/index/${loggedInUser?.id}`}
      >
        <img
          style={{
            objectFit: 'contain',
          }}
          src={logo}
          alt="logo"
        />
      </a>
      {screens.lg && (
        <Title
          level={3}
          style={{
            margin: 0,
            marginLeft: 10,
            color: '#ffffff',
            fontWeight: 'bold',
            fontFamily: '-apple-system',
            whiteSpace: 'nowrap',
          }}
        >
          Universitatea Babes-Bolyai
        </Title>
      )}
    </div>
  );

  const LanguageSelectorDropdown = (
    <Dropdown menu={menuProps}>
      <Button
        type="text"
        ghost
        style={{ paddingLeft: '15px', paddingRight: '0px' }}
      >
        {getFlagByLanguage(i18n.language)}
        <DownOutlined
          style={{
            fontSize: '10px',
            color: 'white',
          }}
        />
      </Button>
    </Dropdown>
  );

  const LogoutButton = (
    <>
      {/* Logout */}
      {loggedInUser && (
        <Button
          type="text"
          onClick={async () => await handleLogout()}
          style={{ width: '50px' }}
          ghost
          icon={
            <RiLogoutBoxRLine
              style={{
                fontSize: '20px',
                color: '#fbfbfb',
                fontWeight: 'bold',
              }}
            />
          }
        ></Button>
      )}
    </>
  );

  const MobileUserProfileMenuButton = (
    <Dropdown
      menu={{ items: userMenuItems }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Avatar style={{ cursor: 'pointer' }} icon={<UserOutlined />} />
    </Dropdown>
  );

  return (
    <>
      {contextHolder}
      <nav style={{ width: '100%', boxSizing: 'border-box' }}>
        <Flex align="middle" justify="space-between">
          <Flex align="middle" style={{ padding: '0 5px' }}>
            {/* Collapse sidebar button */}
            {!props.isLoginPage && !screens.lg && CollapseSideBarButton}
          </Flex>

          <Flex align="middle" justify="center">
            {/* Logo */}
            {Logo}
          </Flex>

          <Flex
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Flex
              justify="space-between"
              align="center"
              gap={10}
              style={{ padding: '0 5px' }}
            >
              {/* User page */}
              {!props.isLoginPage && (
                <>
                  <NotificationCenterBell userId={loggedInUser?.id} />

                  {/* Desktop view */}
                  {screens.lg
                    ? // Profile button
                      !props.isLoginPage && ProfileButton
                    : // Mobile view
                      MobileUserProfileMenuButton}
                </>
              )}

              {/* Change theme button */}
              {(screens.lg || props.isLoginPage) && ThemeSwitchButton}

              {/* Select language button */}
              {(screens.lg || props.isLoginPage) && LanguageSelectorDropdown}

              {/* Logout button */}
              {!props.isLoginPage && screens.lg && LogoutButton}
            </Flex>
          </Flex>
        </Flex>
      </nav>

      {ModalForProfile}
    </>
  );
};

export default HeaderComponent;
